/**
 * Sidebar navigation model (ARCHITECTURE.md §8).
 *
 * Data-driven so the shell renders one `<Link>` per entry and feature gates are
 * applied uniformly. `gate` names the feature flag (`{ ai, toss, push }` from
 * `/api/settings`) a section needs; when locked the item still renders with a
 * lock affordance and routes to a CTA state (pages own that copy). `to` is typed
 * as the literal route paths so it stays assignable to the typed `<Link to>`.
 */
import type { ComponentType, SVGProps } from "react";
import type { FeatureFlags } from "@richrich/contracts";
import type { MessageKey } from "@richrich/i18n";
import {
  ApprovalsIcon,
  ChatIcon,
  DashboardIcon,
  MarketIcon,
  NotificationsIcon,
  OrdersIcon,
  PortfolioIcon,
  SettingsIcon,
  StrategiesIcon,
} from "../components/icons";

/** The subset of top-level routes reachable from the rail. */
export type NavTo =
  | "/"
  | "/portfolio"
  | "/market"
  | "/strategies"
  | "/chat"
  | "/orders"
  | "/approvals"
  | "/notifications"
  | "/settings";

export interface NavItem {
  /** i18n key (`nav.*`) resolved to the visible label by the rendering shell. */
  labelKey: MessageKey;
  to: NavTo;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  /** Feature flag required to fully use this section, if any. */
  gate?: keyof FeatureFlags;
}

/** Primary sections (top of the rail). */
export const PRIMARY_NAV: readonly NavItem[] = [
  { labelKey: "nav.dashboard", to: "/", icon: DashboardIcon },
  { labelKey: "nav.portfolio", to: "/portfolio", icon: PortfolioIcon },
  { labelKey: "nav.market", to: "/market", icon: MarketIcon },
  { labelKey: "nav.strategies", to: "/strategies", icon: StrategiesIcon },
  { labelKey: "nav.chat", to: "/chat", icon: ChatIcon, gate: "ai" },
  { labelKey: "nav.orders", to: "/orders", icon: OrdersIcon, gate: "toss" },
  { labelKey: "nav.approvals", to: "/approvals", icon: ApprovalsIcon, gate: "push" },
  { labelKey: "nav.notifications", to: "/notifications", icon: NotificationsIcon },
];

/** Footer sections (bottom of the rail). */
export const FOOTER_NAV: readonly NavItem[] = [
  { labelKey: "nav.settings", to: "/settings", icon: SettingsIcon },
];

/** Whether a nav item is locked given the current feature flags. */
export function isLocked(item: NavItem, features: FeatureFlags | undefined): boolean {
  if (item.gate === undefined) return false;
  if (features === undefined) return false;
  return !features[item.gate];
}
